/**
 * ArchivOS design tokens.
 *
 * Source: the "Variables" panel of the XD specs mode (DESIGN.md, "Design
 * system" section, extracted on 21/08/2026). The XD names are `--unnamed-*`;
 * here they receive semantic names and the original name is kept in a comment
 * so that each value can be traced back to the design file.
 *
 * This module does not depend on MUI: it is consumable from the theme, from
 * `sx`, from CSS-in-JS or from anywhere else. The MUI theme (`./index.ts`)
 * is built on top of it.
 */

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

export const colors = {
  /** `--unnamed-color-00409d` — primary blue: login and modal background,
   * large button text, text entered into fields. */
  primary: '#00409D',
  /** `--unnamed-color-181c35` — near-black blue: base text colour. */
  textBase: '#181C35',
  /** `--unnamed-color-e76c4f` — primary orange: table headers,
   * primary buttons. */
  accent: '#E76C4F',
  /** `--unnamed-color-ff8b6f` — light orange: links and text buttons. */
  accentLight: '#FF8B6F',
  /** `--unnamed-color-f5f5f5` — very light grey: application background, inactive
   * tiles. */
  surface: '#F5F5F5',
  /** `--unnamed-color-ffffff` — white: text over blue, field background. */
  white: '#FFFFFF',
  /** `--unnamed-color-cfcfcf` — medium grey: borders. */
  border: '#CFCFCF',
  /** `--unnamed-color-cccccc` — grey: field help text (`info-champ`). */
  hint: '#CCCCCC',
  /** `--unnamed-color-b5b5b7` — warm grey: secondary text. */
  muted: '#B5B5B7',
} as const;

// ---------------------------------------------------------------------------
// Typographic families
// ---------------------------------------------------------------------------

/**
 * Poppins is loaded from Google Fonts in `public/index.html` (weights 400, 500,
 * 600 and 700, which are the ones used by the 12 XD styles).
 *
 * Charter is NOT licensed in the project. The stack declares Charter first so
 * that, the day it is licensed, it is enough to add the corresponding
 * `@font-face` without touching any component. In the meantime the browser
 * falls back to Source Serif 4 (free, also in `index.html`, weight 700: the
 * two XD serif styles are bold) and, if that were missing too, to Georgia.
 */
export const fontFamilies = {
  sans: '"Poppins", "Helvetica Neue", Arial, sans-serif',
  serif: '"Charter", "Source Serif 4", Georgia, serif',
} as const;

/**
 * Weights. The XD exports `--unnamed-font-weight-600` as `600px`; it is an
 * export artefact, the correct value is `600` without a unit.
 */
export const fontWeights = {
  regular: 400, // `--unnamed-font-weight-normal`
  medium: 500, // `--unnamed-font-weight-medium`
  semiBold: 600, // `--unnamed-font-weight-600` (exported as "600px")
  bold: 700, // `--unnamed-font-weight-bold`
} as const;

// ---------------------------------------------------------------------------
// Typographic scale (px)
// ---------------------------------------------------------------------------

/** XD font sizes. `--unnamed-font-size-*`. */
export const fontSizes = {
  xxs: 11,
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  display: 60,
} as const;

/** XD line heights. `--unnamed-line-spacing-*`. */
export const lineHeights = {
  xxs: 17,
  xs: 18,
  sm: 20,
  md: 21,
  lg: 25,
  display: 40,
} as const;

/** Letter spacing: 0 in all XD styles. */
export const letterSpacing = 0;

/** Browser base for converting px → rem. */
const REM_BASE = 16;

/** `px` → `rem` string, so that typography respects the user's zoom. */
export const rem = (px: number): string => `${px / REM_BASE}rem`;

/**
 * Line height expressed without a unit (relative to the font size). It is the
 * form MUI recommends: if a screen adjusts `fontSize` via `sx`, the
 * line height scales with it instead of staying fixed in px.
 */
export const leading = (lineHeightPx: number, fontSizePx: number): number =>
  Number((lineHeightPx / fontSizePx).toFixed(4));

// ---------------------------------------------------------------------------
// The 12 XD character styles
// ---------------------------------------------------------------------------

export interface CharacterStyle {
  fontFamily: string;
  fontWeight: number;
  /** In rem (see `rem`). */
  fontSize: string;
  /** Without a unit (see `leading`). */
  lineHeight: number;
  letterSpacing: number;
  color: string;
}

const style = (
  fontFamily: string,
  fontWeight: number,
  fontSizePx: number,
  lineHeightPx: number,
  color: string
): CharacterStyle => ({
  fontFamily,
  fontWeight,
  fontSize: rem(fontSizePx),
  lineHeight: leading(lineHeightPx, fontSizePx),
  letterSpacing,
  color,
});

/**
 * 1:1 transcription of the "Defined character styles" table from DESIGN.md.
 * The name of each key is semantic; the original XD name goes in the
 * comment. The colour is part of the style in the XD and is kept here,
 * but the MUI theme does NOT propagate it to the standard variants (h1, body1…):
 * those variants are used over light and dark backgrounds alike, and the
 * white that the XD assigns to `titre-app`, `en-tete` or `champ-blanc` only
 * makes sense over blue. Each screen applies the colour according to its background.
 */
export const characterStyles = {
  /** `.titre-app` — Charter bold 60/40, white. "archiv.OS" brand. */
  appTitle: style(fontFamilies.serif, fontWeights.bold, fontSizes.display, lineHeights.display, colors.white),
  /** `.en-tete` — Charter bold 20/25, white. Modal headings. */
  heading: style(fontFamilies.serif, fontWeights.bold, fontSizes.xl, lineHeights.lg, colors.white),
  /** `.en-tête-tableau` — Poppins 600 16/25, white. Table header. */
  tableHeader: style(fontFamilies.sans, fontWeights.semiBold, fontSizes.md, lineHeights.lg, colors.white),
  /** `.texte-base` — Poppins normal 16/25, #181C35. Regular text. */
  body: style(fontFamilies.sans, fontWeights.regular, fontSizes.md, lineHeights.lg, colors.textBase),
  /** `.texte-gros-bouton` — Poppins medium 18/20, #00409D. Large button. */
  buttonLarge: style(fontFamilies.sans, fontWeights.medium, fontSizes.lg, lineHeights.sm, colors.primary),
  /** `.saisie-champ` — Poppins normal 16/25, #00409D. Text entered into a field. */
  fieldInput: style(fontFamilies.sans, fontWeights.regular, fontSizes.md, lineHeights.lg, colors.primary),
  /** `.info-champ` — Poppins normal 16/25, #CCCCCC. Placeholder / field help. */
  fieldHint: style(fontFamilies.sans, fontWeights.regular, fontSizes.md, lineHeights.lg, colors.hint),
  /** `.champ-blanc` — Poppins normal 16/25, white. Level 1 field (on blue). */
  fieldOnPrimary: style(fontFamilies.sans, fontWeights.regular, fontSizes.md, lineHeights.lg, colors.white),
  /** `.champ-niv-2` — Poppins normal 14/21, white. Level 2 field. */
  fieldOnPrimaryLevel2: style(fontFamilies.sans, fontWeights.regular, fontSizes.sm, lineHeights.md, colors.white),
  /** `.champ-blanc-niv-3` — Poppins normal 12/18, white. Level 3 field. */
  fieldOnPrimaryLevel3: style(fontFamilies.sans, fontWeights.regular, fontSizes.xs, lineHeights.xs, colors.white),
  /** `.lien-bouton-niv-1` — Poppins bold 14/21, #FF8B6F. Link / text button. */
  actionLink: style(fontFamilies.sans, fontWeights.bold, fontSizes.sm, lineHeights.md, colors.accentLight),
  /** `.lien-bouton-niv2` — Poppins bold 11/17, #FF8B6F. Small link. */
  actionLinkSmall: style(fontFamilies.sans, fontWeights.bold, fontSizes.xxs, lineHeights.xxs, colors.accentLight),
} as const;

// ---------------------------------------------------------------------------
// Breakpoints (ROADMAP.md, Part 2, "Responsive decisions")
// ---------------------------------------------------------------------------

/**
 * Three ranges: mobile `< 768`, tablet `768–1199`, desktop `>= 1200`.
 *
 * They replace the former `MEDIUM_SIZE_SCREEN_WIDTH` (899.95) and
 * `INTERMEDIATE_SIZE_SCREEN_WIDTH` (1401) from `src/constants.ts`, removed
 * with the pre-redesign pages that read them. See the note in `./index.ts`
 * about mapping these values to the theme breakpoints.
 */
export const breakpoints = {
  tablet: 768,
  desktop: 1200,
} as const;

// ---------------------------------------------------------------------------
// Radii and shadows — NOT XD tokens
// ---------------------------------------------------------------------------

/**
 * The XD Variables panel does not define radii nor shadows (DESIGN.md, gap 9).
 * The values below are provisional and conservative; they need to be measured
 * against the artboards before considering the design system final.
 *
 * `base` and `button` reproduce what the previous theme already applied
 * (`shape.borderRadius: 10` and `MuiButton.borderRadius: 15`), so that the
 * current screens do not change shape.
 */
export const radii = {
  /** MUI's `shape.borderRadius` (inputs, paper, menus…). Inherited. */
  base: 10,
  /** Buttons. Inherited from the previous theme. */
  button: 15,
  /** Conservative value for new components (chips, tiles, cards). */
  card: 8,
} as const;

/** Provisional shadows; pending measurement against the artboards. */
export const shadows = {
  card: '0 2px 8px rgba(24, 28, 53, 0.08)',
  modal: '0 8px 24px rgba(24, 28, 53, 0.16)',
} as const;

const tokens = {
  colors,
  fontFamilies,
  fontWeights,
  fontSizes,
  lineHeights,
  letterSpacing,
  characterStyles,
  breakpoints,
  radii,
  shadows,
};

export default tokens;
